/**
 * JSON codec that survives `Uint8Array` (见 18 传输层).
 *
 * The RPC layer is transport-agnostic: over a `MessagePort` the structured clone
 * algorithm carries typed arrays natively, but the NDJSON (external process) and
 * debug-WebSocket transports serialize with `JSON.stringify`, which turns a
 * `Uint8Array` into `{"0":1,"1":2,…}` — silently, and unrecoverably on the far
 * side. Those two transports use this codec instead, so the binary fs capability
 * behaves the same everywhere rather than working in production and breaking the
 * moment an iframe page is attached to the debug bridge.
 *
 * Encoding is base64 (+33% on the wire). That cost is accepted because it only
 * lands on the two text transports, neither of which is a hot path — the API
 * shape is not bent to accommodate the weakest transport.
 *
 * Base64 is implemented here rather than via `btoa`/`Buffer`: this package is
 * runtime-neutral (Node main, utilityProcess, browser page, SharedWorker,
 * ServiceWorker), and the `btoa` route additionally needs a latin1 string, which
 * for a multi-megabyte payload means either chunking or a stack overflow.
 */

/** Property name marking an encoded byte array. */
export const BYTES_JSON_TAG = '$bytes';

const ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Reverse alphabet; `-1` marks a character that is not base64 data. */
const LOOKUP = ((): Int8Array => {
  const table = new Int8Array(128).fill(-1);
  for (let i = 0; i < ALPHABET.length; i += 1) {
    table[ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

/** Encode bytes as standard (padded) base64. */
export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      ALPHABET[(chunk >>> 18) & 63] +
      ALPHABET[(chunk >>> 12) & 63] +
      ALPHABET[(chunk >>> 6) & 63] +
      ALPHABET[chunk & 63];
  }
  const remaining = bytes.length - i;
  if (remaining === 1) {
    const chunk = bytes[i] << 16;
    out += `${ALPHABET[(chunk >>> 18) & 63]}${ALPHABET[(chunk >>> 12) & 63]}==`;
  } else if (remaining === 2) {
    const chunk = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out += `${ALPHABET[(chunk >>> 18) & 63]}${ALPHABET[(chunk >>> 12) & 63]}${
      ALPHABET[(chunk >>> 6) & 63]
    }=`;
  }
  return out;
}

/**
 * Decode base64 into bytes. Padding and any non-alphabet character (whitespace,
 * newlines) are ignored rather than rejected — the input always comes from our
 * own encoder, so being lenient costs nothing and avoids a class of transport
 * whitespace bugs.
 */
export function base64ToBytes(text: string): Uint8Array {
  const bytes = new Uint8Array((text.length * 3) >>> 2);
  let written = 0;
  let acc = 0;
  let bits = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    const value = code < 128 ? LOOKUP[code] : -1;
    if (value < 0) {
      continue;
    }
    acc = (acc << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[written] = (acc >>> bits) & 0xff;
      written += 1;
    }
  }
  return written === bytes.length ? bytes : bytes.subarray(0, written);
}

/** Whether `value` is an encoded byte array produced by {@link stringifyWithBytes}. */
function isEncodedBytes(value: unknown): value is { $bytes: string } {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const keys = Object.keys(value);
  return (
    keys.length === 1 &&
    keys[0] === BYTES_JSON_TAG &&
    typeof (value as Record<string, unknown>)[BYTES_JSON_TAG] === 'string'
  );
}

/**
 * `JSON.stringify` that encodes any `Uint8Array` (including a Node `Buffer`,
 * which subclasses it) as `{ "$bytes": "<base64>" }`, at any depth.
 */
export function stringifyWithBytes(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) =>
    item instanceof Uint8Array
      ? { [BYTES_JSON_TAG]: bytesToBase64(item) }
      : item,
  );
}

/**
 * `JSON.parse` that restores `{ "$bytes": … }` back into a `Uint8Array`.
 *
 * A payload that legitimately contains an object whose only key is `$bytes` with
 * a string value would be revived as bytes. Accepted: the tag is namespaced by
 * its `$` prefix, the shape check is exact, and both ends of these transports
 * are ours.
 */
export function parseWithBytes(text: string): unknown {
  return JSON.parse(text, (_key, item: unknown) =>
    isEncodedBytes(item) ? base64ToBytes(item[BYTES_JSON_TAG]) : item,
  );
}
